import { Button, Dialog } from "@sarva/ui";
import { useEffect, useRef, useState } from "react";
import { signOut } from "@/shared/api";
import { restoreSession } from "../api/restore-session";

/**
 * Ticket 8599 — "my session expires about 30 minutes after I sign in, while I'm working".
 *
 * The server ends a session that has not been used for 30 minutes (backend D254) and measures
 * "used" by `POST /auth/refresh`. The access token also lives 30 minutes, and this client
 * refreshed only after a request failed with `401` — so a person working steadily made their
 * first refresh at the 30-minute mark, by which time the server already counted the session as
 * idle, and they were signed out mid-task.
 *
 * So activity is what keeps a session alive, and the server hears about it:
 *
 * - a click or a key press counts as activity (saves are clicks);
 * - while there has been activity since the last refresh, the session is refreshed at most every
 *   five minutes, which keeps the server's idle clock within five minutes of the real last use;
 * - after 25 minutes with no activity a warning offers "Stay signed in";
 * - at 30 minutes with none, this tab signs out and says the session expired.
 *
 * The 12-hour absolute limit stays the server's; a refresh past it fails and signs out.
 */
export const REFRESH_EVERY_MS = 5 * 60_000;
export const WARN_AFTER_IDLE_MS = 25 * 60_000;
export const SIGN_OUT_AFTER_IDLE_MS = 30 * 60_000;
const TICK_MS = 15_000;

export function SessionKeepAlive({ tenantId }: { tenantId: string }) {
  const lastActivity = useRef(Date.now());
  const lastRefresh = useRef(Date.now());
  const refreshing = useRef(false);
  const [warning, setWarning] = useState(false);

  useEffect(() => {
    const touch = () => {
      lastActivity.current = Date.now();
    };
    window.addEventListener("pointerdown", touch, { passive: true });
    window.addEventListener("keydown", touch, { passive: true });
    return () => {
      window.removeEventListener("pointerdown", touch);
      window.removeEventListener("keydown", touch);
    };
  }, []);

  useEffect(() => {
    const refresh = async () => {
      if (refreshing.current) return;
      refreshing.current = true;
      try {
        // `restoreSession` is the one refresh path: it stores the new token, and resolves
        // `false` on a refused refresh (idle, revoked, past 12 hours) rather than throwing.
        const ok = await restoreSession(tenantId);
        if (ok) lastRefresh.current = Date.now();
        else signOut("expired");
      } finally {
        refreshing.current = false;
      }
    };
    const tick = () => {
      const now = Date.now();
      const idle = now - lastActivity.current;
      if (idle >= SIGN_OUT_AFTER_IDLE_MS) {
        setWarning(false);
        signOut("expired");
        return;
      }
      setWarning(idle >= WARN_AFTER_IDLE_MS);
      if (lastActivity.current > lastRefresh.current && now - lastRefresh.current >= REFRESH_EVERY_MS) {
        void refresh();
      }
    };
    const id = window.setInterval(tick, TICK_MS);
    return () => window.clearInterval(id);
  }, [tenantId]);

  const stay = () => {
    lastActivity.current = Date.now();
    setWarning(false);
    void (async () => {
      const ok = await restoreSession(tenantId);
      if (ok) lastRefresh.current = Date.now();
      else signOut("expired");
    })();
  };

  return (
    <Dialog
      open={warning}
      onOpenChange={(open) => {
        if (!open) stay();
      }}
      title="You'll be signed out soon"
      description="For security, a session with no activity ends after 30 minutes."
      footer={
        <Button variant="primary" onClick={stay}>
          Stay signed in
        </Button>
      }
    >
      <p className="text-sm text-secondary">
        Nothing has been clicked or typed for 25 minutes. Anything you have not saved will be lost
        when the session ends.
      </p>
    </Dialog>
  );
}
